import { useEffect, useState } from "react";
import { CheckCircle2, CreditCard, ExternalLink, RefreshCw, ShieldCheck, Unplug } from "lucide-react";
import { request } from "./FunctionalApp";

type Provider = {
  provider: "STRIPE" | "SQUARE";
  available: boolean;
  mode: "SANDBOX" | "LIVE";
  connection: null | { status: "PENDING" | "CONNECTED" | "RESTRICTED"; providerAccountId: string | null; connectedAt: string | null };
};

function ProviderConnections() {
  const [providers, setProviders] = useState<Provider[]>([]);
  const [busy, setBusy] = useState("");
  const [message, setMessage] = useState("");
  async function load() {
    const response = await request("/api/billing/providers");
    if (response.ok) setProviders((await response.json()).providers);
  }
  useEffect(() => {
    const result = new URLSearchParams(location.search).get("payment-provider");
    async function returned() {
      if (result === "stripe-returned") {
        await request("/api/billing/providers/stripe/complete", { method: "POST" });
        setMessage("Stripe account connected.");
        history.replaceState({}, "", location.pathname);
      } else if (result === "square-connected") {
        setMessage("Square account connected.");
        history.replaceState({}, "", location.pathname);
      }
      await load();
    }
    returned();
  }, []);
  async function connect(provider: Provider["provider"]) {
    setBusy(provider);
    setMessage("");
    const response = await request(`/api/billing/providers/${provider.toLowerCase()}/connect`, { method: "POST" });
    const body = await response.json();
    if (response.ok && body.url) location.assign(body.url);
    else {
      setMessage(body.error || "Unable to start account connection.");
      setBusy("");
    }
  }
  async function disconnect(provider: Provider["provider"]) {
    const name = provider === "STRIPE" ? "Stripe" : "Square";
    if (!confirm(`Disconnect ${name} from this business?`)) return;
    setBusy(provider);
    const response = await request(`/api/billing/providers/${provider.toLowerCase()}`, { method: "DELETE" });
    setMessage(response.ok ? `${name} disconnected.` : "Unable to disconnect provider.");
    setBusy("");
    await load();
  }
  return (
    <section className="func-panel payment-providers">
      <div className="func-table-head">
        <div><h2><CreditCard /> Customer payment providers</h2><p>Connect the business account that should receive appointment payments.</p></div>
        <button className="secondary" onClick={load}><RefreshCw /> Refresh</button>
      </div>
      {message ? <p className="provider-message" role="status">{message}</p> : null}
      <div className="provider-grid">
        {providers.map((item) => {
          const connected = item.connection?.status === "CONNECTED";
          const name = item.provider === "STRIPE" ? "Stripe" : "Square";
          return (
            <article className="provider-card" key={item.provider}>
              <div>
                <strong>{name}</strong>
                <span className={`provider-status ${connected ? "connected" : ""}`}>
                  {connected ? <><CheckCircle2 /> Connected</> : item.connection ? "Setup incomplete" : "Not connected"}
                </span>
              </div>
              <p>{item.mode === "SANDBOX" ? "Test mode — no real customer money will move." : "Live mode — real customer payments."}</p>
              {!item.available ? <small>TaskRoute platform setup is required before this provider can be connected.</small> : null}
              <footer>
                {connected ? (
                  <button className="secondary" disabled={busy === item.provider} onClick={() => disconnect(item.provider)}><Unplug /> Disconnect</button>
                ) : (
                  <button disabled={!item.available || Boolean(busy)} onClick={() => connect(item.provider)}><ExternalLink /> {item.connection ? "Continue setup" : `Connect ${name}`}</button>
                )}
              </footer>
            </article>
          );
        })}
      </div>
      <p className="func-admin-note"><ShieldCheck /> Card numbers and provider passwords are never stored by TaskRoute. Each provider hosts its own secure authorisation flow.</p>
    </section>
  );
}

export function BillingWorkspace() {
  const [data, setData] = useState<any>(null);
  const [error, setError] = useState("");
  async function load() {
    setError("");
    const response = await request("/api/billing/status");
    if (!response.ok) return setError("Unable to load account information.");
    setData(await response.json());
  }
  useEffect(() => { load(); }, []);
  if (!data) return <p className="func-empty">{error || "Loading account…"}</p>;
  return (
    <>
      <div className="func-heading">
        <div>
          <h1>{data.sandbox ? "Sandbox and payments" : "Billing and payments"}</h1>
          <p>{data.sandbox ? "This test group has full TaskRoute access without a subscription charge." : "TaskRoute subscription and customer payment-provider settings."}</p>
        </div>
      </div>
      <section className="func-panel">
        <div className="func-table-head">
          <div>
            <h2>{data.sandbox ? <CheckCircle2 /> : <CreditCard />} {data.sandbox ? "Active sandbox" : data.state}</h2>
            <p>{data.sandbox ? "No TaskRoute subscription payment is required." : data.trialEndsAt ? `Trial ends ${new Date(data.trialEndsAt).toLocaleDateString("en-GB")}` : "Subscription account"}</p>
          </div>
          <strong>ACTIVE</strong>
        </div>
        {data.sandbox ? <p className="func-admin-note"><ShieldCheck /> Payment-failure and reactivation demonstrations remain disabled. Connected providers use their test environments.</p> : null}
      </section>
      <ProviderConnections />
    </>
  );
}
